<script setup lang="ts">

import CustomButton from "@/components/Button/Button.vue";
import {useRouter} from "vue-router";
import {useFavoritesStore} from "@/stores/favorites";
import FavoriteEmpty from "@/assets/icons/favorite_empty.svg";
import FavoriteFilled from "@/assets/icons/favorite_filled.svg";
import {useFormatStore} from "@/stores/formatStore";
import {FreeMode, Keyboard, Mousewheel, Pagination, Thumbs} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/vue";
import "swiper/css";
import "swiper/css/pagination";
import {ref} from "vue";

const router = useRouter();

const favoritesStore = useFavoritesStore();
const formatStore = useFormatStore();

const props = defineProps({
  yacht: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  }
});

const swiperInstance = ref(null);

// called by Swiper when it instantiates
function onSwiper(swiper) {
  swiperInstance.value = swiper;
}

// enable keyboard
function enableKeyboard() {
  const s = swiperInstance.value;
  if (!s) return;
  // ensure the swiper element can receive focus
  s.el?.setAttribute('tabindex', '0');
  s.el?.focus();
  s.keyboard?.enable?.();
}

// disable keyboard
function disableKeyboard() {
  const s = swiperInstance.value;
  if (!s) return;
  s.keyboard?.disable?.();
  try { s.el?.blur?.(); } catch {}
}


</script>


<template>
  <div
      :class="[
      'relative flex w-full items-center justify-center overflow-x-hidden flex-col md:flex-row',
      index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'
    ]"
  >
    <!-- IMAGE  -->
    <div class="w-full md:w-3/5 lg:w-2/5 flex flex-col items-center group">
      <div class="relative w-full sm:h-[450px] overflow-hidden">
        <!-- Swiper for yacht images -->
        <Swiper
            :modules="[Pagination, Thumbs, FreeMode, Mousewheel, Keyboard]"
            :slides-per-view="1"
            :space-between="10"
            :pagination="{ clickable: true, dynamicBullets: true, dynamicMainBullets: 3 }"
            :loop="false"
            :keyboard="{ enabled: false }"
            :mousewheel="{ forceToAxis: true }"
            @swiper="onSwiper"
            @mouseenter="enableKeyboard"
            @mouseleave="disableKeyboard"
            @focus="enableKeyboard"
            @blur="disableKeyboard"
            tabindex="0"
            class="w-full h-full"
        >
          <SwiperSlide
              v-for="(img, i) in (yacht?.images.length > 0 ? yacht.images : [yacht.image_main])"
              :key="i"
              class="overflow-hidden"
          >
            <img
                :src="img.original"
                class="w-full h-full object-cover duration-500 group-hover:scale-105"
            />
          </SwiperSlide>
        </Swiper>
      </div>
    </div>

    <!-- INFO -->
    <div
        :class="[
        'w-full md:max-w-[400px] h-auto bg-background-beige text-primary-darkBlue p-6 shadow-lg z-10',
        index % 2 === 1 ? 'md:mr-[-60px]' : 'md:ml-[-60px]'
      ]"
    >
      <div class="flex flex-col w-full gap-3">
        <!-- Left: Name & Category -->
        <div class="flex justify-between items-start">
          <div>
            <h2 class="responsive-title-5 font-bold">{{ yacht.name }}</h2>
            <p class="responsive-body-xsmall text-bw-midGrey mt-1 uppercase">
              {{ yacht.category }}
            </p>
          </div>
          <div>
            <img
                :src="favoritesStore.isFavorite(yacht.id) ? FavoriteFilled : FavoriteEmpty"
                alt="Favorite"
                class="cursor-pointer"
                @click.stop="favoritesStore.toggleFavorite(yacht)"
            />
          </div>
        </div>

        <div class="flex flex-col sm:flex-row text-bw-midGrey gap-1 sm:gap-6">
          <!-- Length -->
          <div class="flex items-center gap-2 text-nowrap">
            <img src="@/assets/icons/length.png" alt="Length icon" />
            <div class="flex responsive-body-xsmall items-center gap-1">
              <p class="font-bold text-primary-darkBlue">{{ yacht.lengthOverall }}</p>
              <span>Length (m)</span>
            </div>
          </div>

          <!-- Cabins -->
          <div class="flex items-center gap-2">
            <img src="@/assets/icons/cabins.svg" alt="Cabins icon" class="w-5 h-5" />
            <div class="flex responsive-body-xsmall items-center gap-1">
              <p class="font-bold text-primary-darkBlue">{{ yacht.cabins }}</p>
              <span>Cabins</span>
            </div>
          </div>

          <!-- Berths -->
          <div class="flex items-center gap-2">
            <img src="@/assets/icons/berths.svg" alt="Berths icon" class="w-5 h-5" />
            <div class="flex responsive-body-xsmall items-center gap-1">
              <p class="font-bold text-primary-darkBlue">{{ yacht.berths }}</p>
              <span>Berths</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Price -->
      <div class="flex justify-start">
        <div class="mt-4 responsive-body-small">
          <p class="text-bw-midGrey font-medium flex items-center gap-1">
            <img src="@/assets/icons/price.png" alt="Price logo" />
            <span>PRICE/WEEK FROM</span>
          </p>
          <p class="responsive-body-xlarge font-bold text-primary-darkBlue mt-1">
            {{ formatStore.formatPrice(yacht.price) }}
          </p>
        </div>
      </div>

      <!-- Buttons -->
      <div class="mt-6 flex justify-between gap-4">
        <CustomButton
            :outlined="true"
            size="medium"
            :shadow="false"
            class="py-2 px-4"
            @click="$emit('on-view-details-click', yacht.slug)"
        >
          VIEW DETAILS
        </CustomButton>
        <CustomButton
            type="red"
            size="medium"
            class="py-2 px-4"
            @click="$emit('on-enquire-yacht-click', yacht.slug)"
        >
          ENQUIRE YACHT
        </CustomButton>
      </div>
    </div>
  </div>
</template>



<style scoped>

</style>


